import type { VeniceConnection, VeniceConnectionStatus } from "../types";

export const EMPTY_VENICE_CONNECTION: VeniceConnection = {
  connected: false,
  status: "unverified",
  keySuffix: null,
  connectedAt: null,
  expiresAt: null,
  lastCheckedAt: null,
  accessPermitted: false,
  balance: {
    spendableCredits: null,
    bundledCredits: null,
    usdCredits: null,
    diemCredits: null,
  },
  capabilities: { text: false, imageGeneration: false, imageEditing: false },
};

export function formatVeniceCredits(value: number | null, compact = false): string {
  if (value === null || !Number.isFinite(value)) return "—";
  const safe = Math.max(0, value);
  if (compact && safe >= 10_000) return `${(safe / 1_000).toFixed(safe >= 100_000 ? 0 : 1)}k`;
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: safe < 10 ? 2 : 0 }).format(safe);
}

export function veniceStatusLabel(status: VeniceConnectionStatus, connected: boolean): string {
  if (!connected) return "Not connected";
  switch (status) {
    case "ready": return "Ready";
    case "funding_required": return "Credits needed";
    case "reconnect_required": return "Reconnect required";
    case "unverified": return "Checking connection";
  }
}

export function veniceStatusMessage(connection: VeniceConnection): string {
  if (!connection.connected) return "Connect a Venice inference key to use AI features.";
  switch (connection.status) {
    case "ready": return "AI requests use your connected Venice balance.";
    case "funding_required": return connection.accessPermitted
      ? "Add Venice Credits before starting another AI request."
      : "This key cannot spend right now. Check its balance and spending limit in Venice.";
    case "reconnect_required": return "The saved key expired or was revoked. Replace it to continue.";
    case "unverified": return "The last balance check did not complete. Refresh the connection before using AI.";
  }
}

export function isVeniceReady(connection: VeniceConnection | undefined): boolean {
  return connection?.connected === true && connection.status === "ready" && connection.accessPermitted;
}

export function mediaContentUrl(mediaId: string, access?: { characterId?: string; gameId?: string; campaignId?: string; shareToken?: string | null }): string {
  const root = import.meta.env.VITE_API_ROOT ?? "/api/v1";
  const query = new URLSearchParams();
  if (access?.characterId) query.set("characterId", access.characterId);
  if (access?.gameId) query.set("gameId", access.gameId);
  if (access?.campaignId) query.set("campaignId", access.campaignId);
  if (access?.shareToken) query.set("share", access.shareToken);
  const suffix = query.size > 0 ? `?${query.toString()}` : "";
  return `${root}/media/${encodeURIComponent(mediaId)}/content${suffix}`;
}
